import {useTranslation} from '@aboutyou/i18n';
import {Link, useContactRoute} from '@aboutyou/router';
import {
  COLORS,
  FONT_SIZES,
  Text,
  mediaTabletAndDown,
  useShowToast,
} from '@aboutyou/ui-components';
import {BasketMutationError, BasketResponseStatus} from '@aboutyou/user';
import React, {useCallback} from 'react';
import styled from 'styled-components';

interface ErrorMessage {
  title: string;
  subtitle: string;
  additionalText?: {text: string; url?: string}[];
}

const SubtitleWrapper = styled.div`
  display: flex;
  justify-content: center;

  @media ${mediaTabletAndDown} {
    flex-direction: column;
  }
`;

const AdditionalText = styled.span`
  font-size: ${FONT_SIZES.px12};
  margin: 0 4px;
`;

const StyledLink = styled(Link)`
  font-size: ${FONT_SIZES.px12};
  text-decoration: underline;
  color: ${COLORS.Black};
`;

const useErrorMessage = (): ((error?: BasketMutationError) => ErrorMessage) => {
  const {t} = useTranslation();
  const contactRoute = useContactRoute();

  return useCallback(
    (error?: BasketMutationError) => {
      if (BasketResponseStatus.MAX_QUANTITY === error?.code) {
        return {
          title: t(`Sorry, that didn't work`),
          subtitle:
            error.message ||
            t(
              'We are sorry, you have reached the maximum quantity of the product.',
            ),
        };
      }

      if (error?.code && BasketResponseStatus.OUTOFSTOCK.includes(error.code)) {
        return {
          title: t(`Sorry, that didn't work`),
          subtitle:
            error.message ||
            t('We are sorry, the product is no longer available.'),
        };
      }

      if (error?.code && BasketResponseStatus.BLOCKED.includes(error.code)) {
        return {
          title: t(
            'Oops! Our systems detected some strange activity and have blocked your traffic',
          ),
          subtitle: error.message || t('Did we get it wrong?'),
          additionalText: [
            {
              text: '😥',
            },
            {
              text: t('Contact us'),
              url: contactRoute,
            },
            {
              text: t('or email us at:'),
            },
            {
              text: 'customerservice@aboutyou.com',
              url: 'mailto:customerservice@aboutyou.com',
            },
          ],
        };
      }

      if (error?.code === BasketResponseStatus.MAX_ORDER_QUANTITY_REACHED) {
        return {
          title: t('Sorry, an unexpected error occurred'),
          subtitle:
            error.message ||
            t(
              'You have reached the maximum quantity for this product and size',
            ),
        };
      }

      return {
        title: t('Sorry, an unexpected error occurred'),
        subtitle:
          error && error.message && error.failed
            ? error.message
            : t('Could not add the product to your basket.'),
      };
    },
    [contactRoute, t],
  );
};

export const useAddToBasketErrorToast = () => {
  const showToast = useShowToast();
  const getErrorMessage = useErrorMessage();

  return useCallback(
    (error?: BasketMutationError) => {
      const errorMessage = getErrorMessage(error);
      showToast({
        'data-testid': 'addToBasketErrorToast',
        type: 'ERROR',
        content: (
          <>
            <Text size="px14">{errorMessage.title}</Text>
            <SubtitleWrapper>
              <Text size="px12">{errorMessage.subtitle}</Text>
              {errorMessage.additionalText &&
                errorMessage.additionalText.map((message, index) =>
                  message.url ? (
                    <StyledLink key={index} to={message.url}>
                      {message.text}
                    </StyledLink>
                  ) : (
                    <AdditionalText key={index}>{message.text}</AdditionalText>
                  ),
                )}
            </SubtitleWrapper>
          </>
        ),
      });
    },
    [getErrorMessage, showToast],
  );
};
